import { Link } from "@tanstack/react-router";
import type { VideoSelection } from "@unbox/api";
import { CalendarCheck01, InfoCircle } from "@untitledui/icons";
import { useMemo } from "react";
import { useAffectedVideosCount, useUnreviewedCount } from "@/api/assurance/actions";
import { useVideoCollaborations } from "@/api/collaborations";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { InlineTruncateHover } from "@/components/InlineTruncateHover";
import { Checkbox as RadixCheckbox } from "@/components/ui/checkbox";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { formatCreatorId } from "@/utils/formatters";
import { pluralize } from "@/utils/text";

type CollaborationItem = {
  id: string;
  name: string;
  type: "open" | "target";
  typeLabel: string;
  canRemove: boolean;
  productTitle?: string;
  collaborationType?: "STANDARD" | "TOP_CREATOR_PROGRAM";
  creatorCount: number;
  matchesVideo: boolean;
  expired: boolean;
};

type CollaborationTableHeaderProps = {
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: () => void;
  disabled?: boolean;
  removableCount: number;
};

const CollaborationTableHeader = ({
  allSelected,
  someSelected,
  onSelectAll,
  disabled,
  removableCount,
}: CollaborationTableHeaderProps) => (
  <div className="h-11 max-h-11 px-4 py-3 bg-secondary border-b border-secondary flex items-center gap-3 w-full">
    <Checkbox
      isSelected={allSelected}
      isIndeterminate={someSelected}
      onChange={onSelectAll}
      isDisabled={disabled || removableCount === 0}
    />
    <div className="flex-1 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <span className="text-xs font-semibold text-quaternary">Collaboration name</span>
      </div>
      <div className="w-28 shrink-0">
        <span className="text-xs font-semibold text-quaternary">Type</span>
      </div>
    </div>
  </div>
);

type Creator = {
  creatorId: string;
  thumbnail: string | null;
};

type CollaborationSummaryBoxProps = {
  affectedCreators: Creator[];
};

const CollaborationSummaryBox = ({ affectedCreators }: CollaborationSummaryBoxProps) => {
  const summaryText =
    affectedCreators.length === 1 ? (
      <>
        <span className="font-semibold">{formatCreatorId(affectedCreators[0].creatorId)}</span> will be removed from the
        above collaborations
      </>
    ) : (
      <>
        <span className="font-semibold">
          {affectedCreators.length} creator{affectedCreators.length !== 1 ? "s" : ""}
        </span>{" "}
        will be removed from the above collaborations
      </>
    );

  return (
    <div className="flex items-center gap-5 px-6 py-4 bg-tertiary rounded-lg min-h-[72px]">
      <div className="flex items-center shrink-0">
        {affectedCreators.slice(0, 2).map((creator, index) => (
          <Avatar key={creator.creatorId} size="md" src={creator.thumbnail} className={cx(index > 0 && "-ml-2")} />
        ))}
        {affectedCreators.length > 2 && (
          <div className="size-10 rounded-full bg-primary flex items-center justify-center text-base font-semibold text-quaternary outline outline-1 -outline-offset-1 outline-secondary -ml-2">
            {affectedCreators.length - 2}
          </div>
        )}
      </div>
      <p className="flex-1 text-sm font-medium text-secondary">{summaryText}</p>
    </div>
  );
};

const ExpiredCollaborationNotice = ({
  expiredCollaborations,
  merchantId,
}: {
  expiredCollaborations: CollaborationItem[];
  merchantId: string;
}) => (
  <div className="flex flex-col items-center gap-3 bg-warning-50 rounded-lg px-6 py-8 text-center">
    <CalendarCheck01 className="w-8 h-8 text-tertiary" />
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-primary">
        The collaboration{expiredCollaborations.length > 1 ? "s" : ""} this video belongs to{" "}
        {expiredCollaborations.length > 1 ? "have" : "has"} expired
      </p>
      <p className="text-sm text-tertiary">
        {expiredCollaborations.map((collab, index) => (
          <span key={collab.id}>
            {index > 0 && ", "}
            <Link
              to={`/merchant/$merchantId/collaborations/${collab.type}/$collaborationId`}
              params={{ merchantId, collaborationId: collab.id }}
              className="text-sm text-brand-primary underline underline-offset-2 hover:text-brand-secondary"
              target="_blank"
            >
              {collab.name}
            </Link>
          </span>
        ))}{" "}
        {expiredCollaborations.length > 1 ? "are" : "is"} no longer active
      </p>
    </div>
  </div>
);

type AutoApproveCheckboxProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  unreviewedCount: number;
  unreviewedVideoCount: number;
  showError: boolean;
};

const AutoApproveCheckbox = ({
  checked,
  onCheckedChange,
  unreviewedCount,
  unreviewedVideoCount,
  showError,
}: AutoApproveCheckboxProps) => (
  <div className="flex flex-col gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
    <div className="flex items-center gap-3">
      <RadixCheckbox
        id="auto-approve-risks"
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className={showError && !checked ? "border-destructive border-2" : ""}
      />
      <label htmlFor="auto-approve-risks" className="flex-1 text-sm font-medium text-secondary cursor-pointer">
        Auto-approve risks in these creators' videos
      </label>
      <Tooltip
        title="Some of these videos have risks which have not been reviewed. Skipping the review means creators may be notified for risks that don't exist. If incorrect approving this can also reduce the accuracy of future risk detection over time."
        placement="top"
      >
        <TooltipTrigger>
          <InfoCircle className="w-5 h-5 text-tertiary" />
        </TooltipTrigger>
      </Tooltip>
    </div>
    {checked && (
      <p className="text-sm text-tertiary pl-7">
        This will approve <strong>{pluralize(unreviewedCount, "risk", "risks")}</strong> across{" "}
        <strong>{pluralize(unreviewedVideoCount, "video", "videos")}</strong> without review
      </p>
    )}
    {showError && !checked && (
      <p className="text-sm text-destructive pl-7">Select checkbox or review risks manually to send message</p>
    )}
  </div>
);

type RemoveFromCollaborationFormFieldsProps = {
  videoSelection: VideoSelection;
  videoProductIds?: string[];
  creators: Creator[];
  disabled?: boolean;
  selectedCollaborationIds: string[];
  onSelectionChange: (ids: string[]) => void;
  autoApproveChecked: boolean;
  onAutoApproveChange: (checked: boolean) => void;
  showAutoApproveError: boolean;
};

export const RemoveFromCollaborationFormFields = ({
  videoSelection,
  videoProductIds,
  creators,
  disabled,
  selectedCollaborationIds,
  onSelectionChange,
  autoApproveChecked,
  onAutoApproveChange,
  showAutoApproveError,
}: RemoveFromCollaborationFormFieldsProps) => {
  const merchantId = useMerchant();
  const { data: collaborationInfo, isLoading, error } = useVideoCollaborations(videoSelection);
  const { data: unreviewedData } = useUnreviewedCount(videoSelection);

  const videoIds = videoSelection.mode === "include" ? videoSelection.videoIds : [];

  const now = useMemo(() => new Date(), []);

  const allCollaborations = useMemo((): CollaborationItem[] => {
    if (!collaborationInfo) return [];

    const items: CollaborationItem[] = [];
    const productSet = videoProductIds && videoProductIds.length > 0 ? new Set(videoProductIds) : null;

    for (const collab of collaborationInfo.openCollaborations) {
      const isExpired = !!collab.commissionEndTime && new Date(collab.commissionEndTime) < now;
      items.push({
        id: collab.collaborationId,
        name: collab.productName,
        type: "open",
        typeLabel: "Open",
        canRemove: true,
        productTitle: collab.productName,
        creatorCount: collab.creatorCount,
        matchesVideo: productSet ? productSet.has(collab.productId) : false,
        expired: isExpired,
      });
    }

    for (const collab of collaborationInfo.targetCollaborations) {
      const isExpired = !!collab.endTime && new Date(collab.endTime) < now;
      items.push({
        id: collab.collaborationId,
        name: collab.name,
        type: "target",
        typeLabel: "Targeted",
        canRemove: true,
        creatorCount: collab.creatorCount,
        matchesVideo: productSet ? collab.productIds.some((pid) => productSet.has(pid)) : false,
        expired: isExpired,
      });
    }

    return items;
  }, [collaborationInfo, videoProductIds, now]);

  const { activeCollaborations, expiredCollaborations } = useMemo(() => {
    const active: CollaborationItem[] = [];
    const expired: CollaborationItem[] = [];
    for (const collab of allCollaborations) {
      if (collab.expired) {
        expired.push(collab);
      } else {
        active.push(collab);
      }
    }
    return { activeCollaborations: active, expiredCollaborations: expired };
  }, [allCollaborations]);

  const isSingleVideo = videoIds.length === 1;

  const sortedCollaborations = useMemo(() => {
    const collabs = isSingleVideo ? activeCollaborations : allCollaborations;
    return [...collabs].sort((a, b) => {
      if (a.matchesVideo !== b.matchesVideo) return a.matchesVideo ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [activeCollaborations, allCollaborations, isSingleVideo]);

  const allExpired = isSingleVideo && allCollaborations.length > 0 && activeCollaborations.length === 0;

  const handleToggle = (id: string, canRemove: boolean) => {
    if (!canRemove || disabled) return;

    if (selectedCollaborationIds.includes(id)) {
      onSelectionChange(selectedCollaborationIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedCollaborationIds, id]);
    }
  };

  const handleSelectAll = () => {
    const removableIds = sortedCollaborations.filter((c) => c.canRemove).map((c) => c.id);
    const allSelected = removableIds.every((id) => selectedCollaborationIds.includes(id));

    if (allSelected) {
      onSelectionChange(selectedCollaborationIds.filter((id) => !removableIds.includes(id)));
    } else {
      const newIds = new Set([...selectedCollaborationIds, ...removableIds]);
      onSelectionChange([...newIds]);
    }
  };

  const removableCount = sortedCollaborations.filter((c) => c.canRemove).length;
  const selectedRemovableCount = sortedCollaborations.filter(
    (c) => c.canRemove && selectedCollaborationIds.includes(c.id),
  ).length;
  const allSelected = removableCount > 0 && selectedRemovableCount === removableCount;
  const someSelected = selectedRemovableCount > 0 && selectedRemovableCount < removableCount;

  const { affectedCreatorIds, affectedProductIds } = useMemo(() => {
    if (!collaborationInfo || selectedCollaborationIds.length === 0) {
      return { affectedCreatorIds: [], affectedProductIds: [] };
    }

    const creatorIds = new Set<string>();
    const productIds = new Set<string>();

    for (const collabId of selectedCollaborationIds) {
      const openCollab = collaborationInfo.openCollaborations.find((c) => c.collaborationId === collabId);
      if (openCollab) {
        productIds.add(openCollab.productId);
        for (const creatorId of openCollab.creatorIds) {
          creatorIds.add(creatorId);
        }
      }

      const targetCollab = collaborationInfo.targetCollaborations.find((c) => c.collaborationId === collabId);
      if (targetCollab) {
        for (const productId of targetCollab.productIds) {
          productIds.add(productId);
        }
        for (const creatorId of targetCollab.creatorIds) {
          creatorIds.add(creatorId);
        }
      }
    }

    return {
      affectedCreatorIds: Array.from(creatorIds),
      affectedProductIds: Array.from(productIds),
    };
  }, [collaborationInfo, selectedCollaborationIds]);

  const affectedCreators = useMemo(() => {
    return creators.filter((c) => affectedCreatorIds.includes(c.creatorId));
  }, [affectedCreatorIds, creators]);

  useAffectedVideosCount(affectedCreatorIds, affectedProductIds);

  const hasUnreviewedRisks = !!unreviewedData && unreviewedData.unreviewedCount > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-center py-12">
          <LoadingIndicator size="sm" label="Loading collaborations..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-center py-12">
          <div className="text-error-primary text-sm">Failed to load collaborations</div>
        </div>
      </div>
    );
  }

  if (!collaborationInfo || allCollaborations.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-center py-12">
          <div className="text-tertiary text-sm">
            No collaborations found for {videoIds.length === 1 ? "this video's creator" : "these videos' creators"}
          </div>
        </div>
      </div>
    );
  }

  if (allExpired) {
    return (
      <div className="flex flex-col gap-2 w-full overflow-hidden">
        <ExpiredCollaborationNotice expiredCollaborations={expiredCollaborations} merchantId={merchantId} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 w-full overflow-hidden">
      <CollaborationTableHeader
        allSelected={allSelected}
        someSelected={someSelected}
        onSelectAll={handleSelectAll}
        disabled={disabled}
        removableCount={removableCount}
      />

      <div className="flex flex-col gap-2 max-h-64 overflow-auto">
        {sortedCollaborations.map((collab) => {
          const isSelected = selectedCollaborationIds.includes(collab.id);
          const canRemove = collab.canRemove;

          return (
            <button
              key={collab.id}
              type="button"
              onClick={() => handleToggle(collab.id, canRemove)}
              disabled={disabled || !canRemove}
              className={cx(
                "p-4 rounded-xl inline-flex items-center gap-3 text-left w-full",
                collab.matchesVideo ? "bg-utility-brand-100" : "bg-primary",
                isSelected
                  ? "outline outline-2 outline-offset-[-2px] outline-brand"
                  : "outline outline-1 outline-offset-[-1px] outline-secondary",
                !canRemove && "opacity-60 cursor-not-allowed",
              )}
            >
              <div className="py-0.5 flex items-center pointer-events-none">
                <Checkbox isSelected={isSelected} isDisabled={!canRemove || disabled} />
              </div>
              <div className="flex-1 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-secondary line-clamp-3">
                      <InlineTruncateHover maxChars={150}>{collab.name}</InlineTruncateHover>
                    </p>
                    {collab.matchesVideo && (
                      <BadgeWithDot size="sm" color="brand">
                        Linked collaboration
                      </BadgeWithDot>
                    )}
                  </div>
                </div>
                <div className="w-28 shrink-0">
                  <p className="text-sm text-tertiary">{collab.typeLabel}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {isSingleVideo && expiredCollaborations.length > 0 && (
        <ExpiredCollaborationNotice expiredCollaborations={expiredCollaborations} merchantId={merchantId} />
      )}

      {selectedCollaborationIds.length > 0 && <CollaborationSummaryBox affectedCreators={affectedCreators} />}

      {selectedCollaborationIds.length > 0 && hasUnreviewedRisks && (
        <AutoApproveCheckbox
          checked={autoApproveChecked}
          onCheckedChange={onAutoApproveChange}
          unreviewedCount={unreviewedData.unreviewedCount}
          unreviewedVideoCount={unreviewedData.unreviewedVideoCount}
          showError={showAutoApproveError}
        />
      )}
    </div>
  );
};
